import type { DataItems } from '@domain/types/entity';
import { resolveSchemas } from '@helpers/resolveSchema';
import { fetchSchema } from '@queries/fetchSchema';
import type { ResolvedSchemaTree } from '@root/helpers/schema';
import {
  flattenResolvedSchemaTree,
  resolveRefs,
  resolveSchemaTreeFromForm,
  schemaTreeToFlatList,
} from '@root/helpers/schema';
import { createSchemaValidator } from '@root/helpers/schema/ajv';
import { getConditionalDriverFields } from '@root/helpers/schema/getConditionalDriverFields';
import { createSchemaValidation } from '@root/helpers/schema/schema-store-validation';
import type { SchemaTreeToFlatListResult } from '@root/helpers/schema/schemaTreeToFlatList';
import type { FormError, JSONSchema } from '@root/helpers/schema/types';
import { isTemplatePath } from '@utils/app-paths';
import { cloneDeep, debounce, isEqual, omit } from 'lodash-es';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { type AutoPopulateRule, buildAutoPopulateRules } from './auto-populate-from-schema';
import { useDatapointsStore } from './datapoints.store';
import type { SchemaValidation, SectionValidationState } from './types';

const VALIDATE_DEBOUNCE_MS = 150;

export type SchemaStoreSlice = {
  autoPopulateRules: AutoPopulateRule[];
  compiledValidator: any;
  conditionalDriverFields: Set<string>;
  dataItems: DataItems | null;
  errors: FormError[] | null;
  findField: (
    id: string,
    shouldIncludeParentNodes?: boolean,
    shouldIncludeConditionalFields?: boolean,
  ) => SchemaTreeToFlatListResult | null | undefined;
  getSectionValidationState: (id: string) => SectionValidationState;
  isValid: boolean;
  isLoading: boolean;
  fetchSchema: () => Promise<JSONSchema>;
  refreshResolvedSchema: (formValues: Record<string, unknown>) => void;
  resetSchemaStore: () => void;
  resolvedRefsSchema: JSONSchema | null;
  resolvedSchema: JSONSchema | null;
  resolvedSchemas: JSONSchema[] | null;
  schema: JSONSchema | null;
  schemaTree: ResolvedSchemaTree[] | null;
  schemaTreeWithConditionalFields: ResolvedSchemaTree[] | null;
  schemaTreeList: SchemaTreeToFlatListResult[] | null;
  schemaTreeListWithParents: SchemaTreeToFlatListResult[] | null;
  schemaTreeListWithConditionalFields: SchemaTreeToFlatListResult[] | null;
  schemaValidation: SchemaValidation;
  updateSchemaStore: (dataItems: DataItems) => void;
  validate: () => void;
  triggerValidation: () => void;
  flushValidation: () => void;
  cancelValidation: () => void;
};

export const useSchemaStore = createWithEqualityFn<SchemaStoreSlice>((set, get) => {
  // Closure-scoped to avoid a module-level forward reference to `useSchemaStore`
  // (the validator reads from the store itself, which breaks TS inference when
  // declared at module top).
  const validateDebounced = debounce(
    () => {
      get().validate();
    },
    VALIDATE_DEBOUNCE_MS,
    { leading: false, trailing: true },
  );

  return {
    schema: null,
    autoPopulateRules: [],
    compiledValidator: null,
    conditionalDriverFields: new Set<string>(),
    isLoading: false,
    resolvedRefsSchema: null,
    dataItems: null,
    schemaTree: null,
    schemaTreeWithConditionalFields: null,
    errors: null,
    schemaTreeList: null,
    schemaTreeListWithParents: null,
    schemaTreeListWithConditionalFields: null,
    resolvedSchema: null,
    resolvedSchemas: null,
    schemaValidation: {},
    isValid: false,
    fetchSchema: async () => {
      try {
        set({ isLoading: true });
        const schema = await fetchSchema();

        // Create the compiled validator from the fetched schema
        const compiledValidator = createSchemaValidator(schema);
        const conditionalDriverFields = getConditionalDriverFields(schema);
        const autoPopulateRules = buildAutoPopulateRules(schema);

        set({ schema, isLoading: false, compiledValidator, conditionalDriverFields, autoPopulateRules });
        return schema;
      } catch (error) {
        console.error('Error fetching schema:', error);
        set({ isLoading: false });
        throw error;
      }
    },
    refreshResolvedSchema: (formValues) => {
      const resolvedRefsSchema = get().resolvedRefsSchema;
      const schemaData = {
        ...resolvedRefsSchema?.properties.SubmissionForm,
        definitions: resolvedRefsSchema?.definitions,
      };

      const clonedSchema = cloneDeep(schemaData);
      const oldResolvedSchemas = get().resolvedSchemas;
      const resolvedSchemas = resolveSchemas(clonedSchema, formValues);

      if (isEqual(oldResolvedSchemas, resolvedSchemas)) {
        return;
      }

      set({ resolvedSchema: resolvedSchemas[0], resolvedSchemas });
    },
    resetSchemaStore: () =>
      set({
        isValid: false,
        resolvedRefsSchema: null,
        errors: null,
        dataItems: null,
        schemaTree: null,
        schemaTreeList: null,
        schemaTreeListWithParents: null,
        schemaValidation: {},
      }),
    updateSchemaStore: (dataItems = {}) => {
      const schema = get().schema;

      if (!schema) {
        console.error('Schema is null');
        return;
      }

      const resolvedRefsSchema = cloneDeep(schema) as JSONSchema;
      resolveRefs(resolvedRefsSchema);

      const isTemplate = isTemplatePath();
      const schemaTree = resolveSchemaTreeFromForm(schema, dataItems, isTemplate);

      const schemaTreeWithConditionalFields = isTemplate
        ? schemaTree
        : resolveSchemaTreeFromForm(schema, dataItems, true);

      const schemaTreeList = flattenResolvedSchemaTree(schemaTree);

      const schemaTreeListWithConditionalFields = flattenResolvedSchemaTree(schemaTreeWithConditionalFields);

      const schemaTreeListWithParents = schemaTreeToFlatList(schemaTree, true);

      set({
        schemaTree,
        schemaTreeWithConditionalFields,
        schemaTreeList,
        schemaTreeListWithParents,
        dataItems,
        resolvedRefsSchema,
        schemaTreeListWithConditionalFields,
      });
    },

    getSectionValidationState: (id: string) => {
      return (
        get().schemaValidation[id] ?? {
          hasErrors: false,
          hasValues: false,
          errors: [],
        }
      );
    },
    validate: () => {
      const formValues = useDatapointsStore.getState().formValues;
      const schemaTreeListWithParents = get().schemaTreeListWithParents;
      const compiledValidator = get().compiledValidator;

      if (!compiledValidator) {
        console.error('Cannot validate: compiled validator not available');
        return;
      }

      const isValid = compiledValidator(formValues);
      // the verbose validator attaches `schema`/`parentSchema`/`data` (full form snapshots)
      // to every error; strip them so errors stay small and content-stable while typing
      const errors =
        (compiledValidator.errors as unknown as FormError[] | null)?.map(
          (error) => omit(error, ['schema', 'parentSchema', 'data']) as FormError,
        ) ?? null;

      const schemaValidation = createSchemaValidation(schemaTreeListWithParents, formValues, errors);

      // preserve refs when the result is unchanged so subscribers don't re-render per keystroke
      const prev = get();
      set({
        schemaValidation: isEqual(prev.schemaValidation, schemaValidation) ? prev.schemaValidation : schemaValidation,
        errors: isEqual(prev.errors, errors) ? prev.errors : errors,
        isValid,
      });
    },
    triggerValidation: () => validateDebounced(),
    flushValidation: () => validateDebounced.flush(),
    cancelValidation: () => validateDebounced.cancel(),
    findField: (id: string, shouldIncludeParentNodes = false, shouldIncludeConditionalFields = false) => {
      const schemaTree = shouldIncludeConditionalFields ? get().schemaTreeWithConditionalFields : get().schemaTree;

      if (!schemaTree) {
        return null;
      }

      const schemaTreeList = schemaTreeToFlatList(schemaTree, shouldIncludeParentNodes);
      return schemaTreeList.find((item: SchemaTreeToFlatListResult) => item.id === id);
    },
  };
}, shallow);
